import React, { useEffect } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { m } from 'framer-motion';

// Components
import RDPHeader from '../Components/Header/RDPHeader';
import RDPFooter from '../Components/Footers/RDPFooter';
import { fadeIn } from '../Functions/GlobalAnimations';
import Buttons from '../Components/Button/Buttons';

const AboutPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      {/* Header Start */}
      <RDPHeader />
      {/* Header End */}

      {/* Hero Section */}
      <section className="pt-[130px] pb-[130px] lg:pt-[120px] lg:pb-[90px] md:pt-[110px] md:pb-[75px] sm:pt-[100px] sm:pb-[50px] bg-white">
        <Container>
          <Row className="justify-center mb-[60px] md:mb-[40px]">
            <Col lg={10} className="text-center">
              <m.span
                className="text-glass-accent font-serif font-medium text-md uppercase block mb-[20px] tracking-[1px]"
                {...fadeIn}
              >
                Depuis 2008
              </m.span>
              <m.h1
                className="font-serif font-bold text-glass-darkest text-[3.5rem] lg:text-[2.8rem] md:text-[2.3rem] sm:text-[1.8rem] mb-[25px] leading-tight"
                {...{ ...fadeIn, transition: { delay: 0.2 } }}
              >
                World Class Packaging
              </m.h1>
              <m.p
                className="text-glass-medium text-xlg lg:text-lg md:text-md w-[85%] mx-auto leading-relaxed"
                {...{ ...fadeIn, transition: { delay: 0.4 } }}
              >
                Réseau professionnel de distribution d'emballages en verre.<br />
                Un accès privilégié à de nombreux verriers Européens et extra-Européens absents du marché Français.
              </m.p>
            </Col>
          </Row>
          
          {/* Stats Cards */}
          <Row className="gap-y-[30px]">
            <Col lg={4} md={6}>
              <m.div
                className="bg-white rounded-[16px] p-[45px] text-center h-full group hover:shadow-xl transition-all duration-300 border border-glass-border"
                {...{ ...fadeIn, transition: { delay: 0.6 } }}
              >
                <i className="line-icon-MaleFemale text-glass-accent text-[48px] mb-[20px] inline-block group-hover:scale-110 transition-transform duration-300"></i>
                <div className="text-[4.5rem] font-bold text-glass-accent font-serif mb-[18px] leading-none">35+</div>
                <h5 className="font-serif font-bold text-glass-darkest mb-[12px] text-[1.3rem] leading-tight">Années<br />d'expertise</h5>
                <p className="text-glass-dark text-[15px] leading-relaxed">Au service des professionnels depuis 2008</p>
              </m.div>
            </Col>
            <Col lg={4} md={6}>
              <m.div
                className="bg-white rounded-[16px] p-[45px] text-center h-full group hover:shadow-xl transition-all duration-300 border border-glass-border"
                {...{ ...fadeIn, transition: { delay: 0.7 } }}
              >
                <i className="line-icon-Handshake text-glass-accent text-[48px] mb-[20px] inline-block group-hover:scale-110 transition-transform duration-300"></i>
                <div className="text-[3.5rem] font-bold text-glass-accent font-serif mb-[18px] leading-none">Nombreux</div>
                <h5 className="font-serif font-bold text-glass-darkest mb-[12px] text-[1.3rem] leading-tight">Partenaires<br />verriers</h5>
                <p className="text-glass-dark text-[15px] leading-relaxed">Europe et extra-Europe</p>
              </m.div>
            </Col>
            <Col lg={4} md={12}>
              <m.div
                className="bg-white rounded-[16px] p-[45px] text-center h-full group hover:shadow-xl transition-all duration-300 border border-glass-border"
                {...{ ...fadeIn, transition: { delay: 0.8 } }}
              >
                <i className="line-icon-Recycling-2 text-glass-accent text-[48px] mb-[20px] inline-block group-hover:scale-110 transition-transform duration-300"></i>
                <div className="text-[4.5rem] font-bold text-glass-accent font-serif mb-[18px] leading-none">100%</div>
                <h5 className="font-serif font-bold text-glass-darkest mb-[12px] text-[1.3rem] leading-tight">Recyclable</h5>
                <p className="text-glass-dark text-[15px] leading-relaxed">Le verre, infiniment recyclable</p>
              </m.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Notre Histoire */}
      <section className="pb-[130px] lg:pb-[90px] md:pb-[75px] sm:pb-[50px] bg-white glass-separator-dots">
        <Container>
          <Row className="items-center mb-[80px]">
            <Col lg={6} md={12} className="mb-[40px] lg:mb-0">
              <m.div {...fadeIn}>
                <span className="text-glass-accent font-serif font-medium text-md uppercase block mb-[15px] tracking-[1px]">
                  Notre Histoire
                </span>
                <h2 className="font-serif font-semibold text-glass-darkest text-[2.5rem] lg:text-[2rem] mb-[25px] leading-tight">
                  Distributeur d'emballages en verre depuis plus de 15 ans
                </h2>
                <p className="text-glass-medium text-lg mb-[20px] leading-relaxed">
                  RDP Glass distribue des solutions d'emballage en verre innovantes et écologiques depuis 2008.
                  Basée en France, notre entreprise s'est imposée comme un acteur incontournable sur les marchés français et belges.
                </p>
                <p className="text-glass-medium text-lg leading-relaxed">
                  Nous accompagnons les professionnels de l'agroalimentaire avec une gamme complète :
                  bouteilles de vin, bière, spiritueux, bocaux, soft drinks et bien plus encore.
                </p>
              </m.div>
            </Col>
            <Col lg={6} md={12}>
              <m.div
                className="relative"
                {...{ ...fadeIn, transition: { delay: 0.3 } }}
              >
                <div className="bg-glass-pearl rounded-[12px] p-[40px]">
                  <h4 className="font-serif font-semibold text-glass-darkest mb-[25px]">Notre Mission</h4>
                  <ul className="space-y-[15px]">
                    <li className="flex items-start gap-[12px]">
                      <i className="feather-check text-glass-accent text-[22px] mt-[2px]"></i>
                      <span className="text-glass-dark text-[16px]">Fournir des emballages en verre de qualité supérieure</span>
                    </li>
                    <li className="flex items-start gap-[12px]">
                      <i className="feather-check text-glass-accent text-[22px] mt-[2px]"></i>
                      <span className="text-glass-dark text-[16px]">Offrir un accès exclusif à notre réseau de verriers européens et extra-européens</span>
                    </li>
                    <li className="flex items-start gap-[12px]">
                      <i className="feather-check text-glass-accent text-[22px] mt-[2px]"></i>
                      <span className="text-glass-dark text-[16px]">Proposer des solutions personnalisées avec conception 3D</span>
                    </li>
                    <li className="flex items-start gap-[12px]">
                      <i className="feather-check text-glass-accent text-[22px] mt-[2px]"></i>
                      <span className="text-glass-dark text-[16px]">Promouvoir l'économie circulaire et la durabilité</span>
                    </li>
                  </ul>
                </div>
              </m.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Nos Valeurs */}
      <section className="pb-[130px] lg:pb-[90px] md:pb-[75px] sm:pb-[50px] bg-white glass-separator-dots">
        <Container>
          <Row className="justify-center mb-[70px]">
            <Col lg={8} className="text-center">
              <m.h2
                className="font-serif font-semibold text-glass-darkest text-[2.5rem] lg:text-[2rem] mb-[20px]"
                {...fadeIn}
              >
                Nos Valeurs
              </m.h2>
              <m.p
                className="text-glass-medium text-lg"
                {...{ ...fadeIn, transition: { delay: 0.2 } }}
              >
                Au service de votre projet depuis plus de 15 ans
              </m.p>
            </Col>
          </Row>
          <Row className="gap-y-[40px]">
            <Col lg={4} md={6}>
              <m.div
                className="bg-white rounded-[12px] p-[40px] text-center h-full"
                {...{ ...fadeIn, transition: { delay: 0.3 } }}
              >
                <i className="line-icon-Medal-3 text-glass-accent text-[50px] mb-[25px] inline-block"></i>
                <h5 className="font-serif font-semibold text-glass-darkest mb-[15px]">Excellence</h5>
                <p className="text-glass-medium text-[15px] leading-relaxed">
                  Des produits de qualité supérieure sélectionnés auprès des meilleurs verriers européens
                </p>
              </m.div>
            </Col>
            <Col lg={4} md={6}>
              <m.div
                className="bg-white rounded-[12px] p-[40px] text-center h-full"
                {...{ ...fadeIn, transition: { delay: 0.4 } }}
              >
                <i className="line-icon-Light-Bulb text-glass-accent text-[50px] mb-[25px] inline-block"></i>
                <h5 className="font-serif font-semibold text-glass-darkest mb-[15px]">Innovation</h5>
                <p className="text-glass-medium text-[15px] leading-relaxed">
                  Conception 3D et maquettes personnalisées pour donner vie à vos projets
                </p>
              </m.div>
            </Col>
            <Col lg={4} md={12}>
              <m.div
                className="bg-white rounded-[12px] p-[40px] text-center h-full"
                {...{ ...fadeIn, transition: { delay: 0.5 } }}
              >
                <i className="line-icon-Recycling-2 text-glass-accent text-[50px] mb-[25px] inline-block"></i>
                <h5 className="font-serif font-semibold text-glass-darkest mb-[15px]">Durabilité</h5>
                <p className="text-glass-medium text-[15px] leading-relaxed">
                  Le verre, matériau 100% recyclable infiniment, pour un avenir plus vert
                </p>
              </m.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Notre Expertise */}
      <section className="pb-[130px] lg:pb-[90px] md:pb-[75px] sm:pb-[50px] bg-white glass-separator-dots">
        <Container>
          <Row className="justify-center mb-[60px]">
            <Col lg={10} className="text-center">
              <m.div {...fadeIn}>
                <span className="text-glass-accent font-serif font-medium text-md uppercase block mb-[15px] tracking-[1px]">
                  Notre Expertise
                </span>
                <h2 className="font-serif font-semibold text-glass-darkest text-[2.5rem] lg:text-[2rem] mb-[20px] leading-tight">
                  Un réseau de partenaires verriers d'exception
                </h2>
                <p className="text-glass-medium text-lg leading-relaxed w-[85%] mx-auto">
                  Nous avons tissé des siCariats solides avec de nombreux verriers européens et extra-européens,
                  vous offrant ainsi un accès privilégié à des produits uniques absents du marché français.
                </p>
              </m.div>
            </Col>
          </Row>

          <Row className="gap-y-[40px]">
            <Col lg={4} md={6}>
              <m.div
                className="bg-glass-pearl rounded-[12px] p-[40px] h-full text-center hover:shadow-lg transition-shadow"
                {...{ ...fadeIn, transition: { delay: 0.2 } }}
              >
                <i className="line-icon-Wine-Bottle text-glass-accent text-[50px] mb-[25px] inline-block"></i>
                <h5 className="font-serif font-semibold text-glass-darkest mb-[15px]">Bouteilles standards et personnalisées</h5>
                <p className="text-glass-medium text-[15px] leading-relaxed">
                  Vin, bière, spiritueux, soft drinks, huiles... Toutes les solutions d'emballage en verre
                </p>
              </m.div>
            </Col>
            <Col lg={4} md={6}>
              <m.div
                className="bg-glass-pearl rounded-[12px] p-[40px] h-full text-center hover:shadow-lg transition-shadow"
                {...{ ...fadeIn, transition: { delay: 0.3 } }}
              >
                <i className="line-icon-Cube-Molecule2 text-glass-accent text-[50px] mb-[25px] inline-block"></i>
                <h5 className="font-serif font-semibold text-glass-darkest mb-[15px]">Conception 3D sur mesure</h5>
                <p className="text-glass-medium text-[15px] leading-relaxed">
                  Créez votre bouteille unique avec nos maquettes 3D et prototypes personnalisés
                </p>
              </m.div>
            </Col>
            <Col lg={4} md={12}>
              <m.div
                className="bg-glass-pearl rounded-[12px] p-[40px] h-full text-center hover:shadow-lg transition-shadow"
                {...{ ...fadeIn, transition: { delay: 0.4 } }}
              >
                <i className="line-icon-Brush text-glass-accent text-[50px] mb-[25px] inline-block"></i>
                <h5 className="font-serif font-semibold text-glass-darkest mb-[15px]">Décoration et personnalisation</h5>
                <p className="text-glass-medium text-[15px] leading-relaxed">
                  Sérigraphie, étiquettes, bouchons personnalisés pour votre marque
                </p>
              </m.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact CTA */}
      <section className="pb-[130px] lg:pb-[90px] md:pb-[75px] sm:pb-[50px] bg-white glass-separator-dots">
        <Container>
          <Row className="justify-center">
            <Col lg={8} className="text-center">
              <m.div {...fadeIn}>
                <h3 className="font-serif font-semibold text-glass-darkest text-[2rem] mb-[20px]">
                  Prêt à démarrer votre projet ?
                </h3>
                <p className="text-glass-medium text-lg mb-[35px]">
                  Notre équipe est à votre disposition pour vous accompagner dans tous vos besoins d'emballage en verre
                </p>
                <div className="flex gap-[15px] justify-center sm:flex-col sm:items-center">
                  <Buttons
                    to="/produits"
                    className="glass-btn-medium font-medium font-serif uppercase text-glass-darkest"
                    size="lg"
                    title="Voir nos produits"
                  />
                  <Buttons
                    to="/contact"
                    className="bg-glass-accent text-white font-medium font-serif uppercase py-[12px] px-[35px] rounded-[8px] hover:bg-glass-accentHover transition-colors inline-block"
                    size="lg"
                    title="Demander un devis"
                  />
                </div>
              </m.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <RDPFooter />
    </div>
  );
};

export default AboutPage;
